import { FC, Fragment, useEffect, useState } from 'react';
import { Button, Form, Modal, Row, Space, Switch, Table, Tooltip } from 'antd';
import {
	InfoCircleOutlined,
	PlusOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/App';
import CardPanel from '@/components/CardPanel';
import { FieldType, renderField } from '@/components/FormBuilder';
import formRule from '@/libs/antd/rules';
import {
	MasterGroup,
	MasterGroupPayload,
	createMasterGroup,
	getMasterGroups,
	updateMasterGroup,
} from '@/libs/fleet-api/services/master-group';

const MASTER_GROUP_FORMS = [
	{
		name: 'name',
		type: 'text',
		label: 'Name',
		config: {
			placeholder: 'Title',
		},
		rules: [formRule.required()],
	},
	{
		name: 'isActive',
		type: 'switch',
		label: 'Status',
		width: '1/2',
		config: {
			checkedChildren: 'Active',
			unCheckedChildren: 'Inactive',
		},
	},
] as FieldType[];

const MasterGroupPage: FC = () => {
	const { notification } = useApp();
	const { data, isLoading, isFetching, refetch } = useQuery({
		queryKey: ['getMasterGroups'],
		queryFn: () => getMasterGroups(),
	});

	const dataSource =
		data?.data.map((value, key) => {
			return {
				...value,
				key,
			};
		}) ?? [];

	const [modalDetailForm] = Form.useForm<MasterGroupPayload>();
	const [modalDetail, setModalDetail] = useState<
		| {
				type: 'create';
		  }
		| {
				type: 'update';
				data: MasterGroup;
		  }
	>();

	useEffect(() => {
		if (modalDetail?.type !== 'update') return;
		modalDetailForm.setFieldsValue(modalDetail.data);
	}, [modalDetail, modalDetailForm]);

	return (
		<Fragment>
			<CardPanel
				title="Master Group"
				extra={
					<Space size="small">
						<Button
							icon={<ReloadOutlined />}
							loading={isFetching}
							onClick={() => refetch()}
						>
							Refresh
						</Button>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								setModalDetail({ type: 'create' });
							}}
						>
							New Entry
						</Button>
					</Space>
				}
				styles={{
					header: {
						paddingRight: 12,
					},
				}}
			>
				<Table
					dataSource={dataSource}
					loading={isLoading || isFetching}
					columns={[
						{
							title: '#',
							align: 'center',
							width: 60,
							render: (_, __, index) => {
								return index + 1;
							},
						},
						{
							title: 'Name',
							render: (_, record) => (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										setModalDetail({
											type: 'update',
											data: record,
										});
									}}
								>
									{record.name}
								</a>
							),
						},
						Table.EXPAND_COLUMN,
						{
							title: 'Status',
							align: 'center',
							dataIndex: 'isActive',
							render: (isActive) => (
								<Switch
									value={isActive}
									checkedChildren="Active"
									unCheckedChildren="Inactive"
									style={{ cursor: 'default' }}
								/>
							),
						},
						{
							title: 'Created',
							align: 'center',
							dataIndex: 'createdDate',
							render: (createdDate) => {
								const date = dayjs(createdDate);
								const shortDate = date.format('DD-MM-YYYY');
								const longDate = date.format('DD MMMM YYYY, HH:mm');

								return (
									<Space>
										<span>{shortDate}</span>
										<Tooltip title={longDate}>
											<InfoCircleOutlined />
										</Tooltip>
									</Space>
								);
							},
						},
						{
							title: 'Updated',
							align: 'center',
							dataIndex: 'createdDate',
							render: (createdDate) => {
								const date = dayjs(createdDate);
								const shortDate = date.format('DD-MM-YYYY');
								const longDate = date.format('DD MMMM YYYY, HH:mm');

								return (
									<Space>
										<span>{shortDate}</span>
										<Tooltip title={longDate}>
											<InfoCircleOutlined />
										</Tooltip>
									</Space>
								);
							},
						},
					]}
					expandable={{
						expandedRowRender(record) {
							return (
								<div style={{ padding: 16 }}>
									<pre style={{ margin: 0 }}>
										{JSON.stringify(record, null, 2)}
									</pre>
								</div>
							);
						},
					}}
				/>
			</CardPanel>

			<Modal
				open={Boolean(modalDetail)}
				width={500}
				title={modalDetail?.type === 'update' ? 'Edit' : 'New'}
				afterClose={() => modalDetailForm.resetFields()}
				onCancel={() => setModalDetail(undefined)}
				onOk={() => modalDetailForm.submit()}
			>
				<Form
					form={modalDetailForm}
					layout="vertical"
					onFinish={async (values) => {
						try {
							if (modalDetail?.type === 'update') {
								await updateMasterGroup(modalDetail.data.id, values);
								notification.success({
									message: 'Updated',
									description: 'data was successfully updated',
								});
							} else {
								await createMasterGroup(values);
								notification.success({
									message: 'Created',
									description: 'data was successfully created',
								});
							}
							setModalDetail(undefined);
							refetch();
						} catch (error) {
							console.log(error);
						}
					}}
				>
					<Row gutter={[16, 16]}>
						{MASTER_GROUP_FORMS.map((field, key) =>
							renderField({ ...field, key }),
						)}
					</Row>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default MasterGroupPage;
